/* ==========================================================================
   Wine Club product page controller
   One custom element per section instance. No globals, no libraries.
   ========================================================================== */

(function () {
  'use strict';

  if (customElements.get('wine-club-product')) return;

  var REDUCED_MOTION =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : { matches: false };

  /* ---------------------------------------------------------------- utils */

  function parseJSON(el, fallback) {
    if (!el) return fallback;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return fallback;
    }
  }

  function normalise(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var fmt = format || '${{amount}}';

    function group(number, precision, thousands, decimal) {
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var head = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var tail = parts[1] ? decimal + parts[1] : '';
      return head + tail;
    }

    var match = fmt.match(placeholder);
    switch (match ? match[1] : 'amount') {
      case 'amount':
        value = group(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = group(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = group(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = group(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = group(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = group(cents, 0, ' ', ',');
        break;
      case 'amount_with_space_separator':
        value = group(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = group(cents, 2, ' ', '.');
        break;
      default:
        value = group(cents, 2, ',', '.');
    }

    return fmt.replace(placeholder, value);
  }

  function bottlesFrom(value) {
    var match = String(value || '').match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  }

  /* ------------------------------------------------------------- component */

  class WineClubProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.controller = new AbortController();
      this.signal = this.controller.signal;

      this.config = parseJSON(this.querySelector('[data-wc-config]'), {}) || {};
      this.variants = parseJSON(this.querySelector('[data-wc-variants]'), []) || [];

      this.form = this.querySelector('[data-wc-form]');
      this.variantInput = this.querySelector('[data-wc-variant-id]');
      this.propertyInput = this.querySelector('[data-wc-frequency-property]');
      this.sellingPlanInput = this.querySelector('[data-wc-selling-plan]');
      this.addButton = this.querySelector('[data-wc-add]');
      this.addText = this.querySelector('[data-wc-add-text]');
      this.errorBox = this.querySelector('[data-wc-error]');
      this.errorText = this.querySelector('[data-wc-error-text]');
      this.live = this.querySelector('[data-wc-live]');
      this.summary = this.querySelector('[data-wc-summary]');
      this.priceRoot = this.querySelector('[data-wc-price-root]');
      this.sticky = this.querySelector('[data-wc-sticky]');

      this.memIndex = typeof this.config.membershipIndex === 'number' ? this.config.membershipIndex : -1;
      this.freqIndex = typeof this.config.frequencyIndex === 'number' ? this.config.frequencyIndex : -1;
      this.wineIndex = typeof this.config.wineIndex === 'number' ? this.config.wineIndex : -1;
      this.freqSource = this.config.frequencySource || 'option';
      this.moneyFormat = this.config.moneyFormat || '${{amount}}';
      this.strings = this.config.strings || {};
      this.busy = false;
      this.successTimer = null;

      this.currentVariant = this.findVariantById(this.config.selectedVariantId) || null;

      this.buildSelectors();
      this.bindSelectors();
      this.bindDescription();
      this.bindGallery();
      this.bindSticky();
      this.bindForm();
      this.setupAnimation();

      this.refresh({ silent: true });
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      if (this.successTimer) clearTimeout(this.successTimer);
      if (this.slideObserver) this.slideObserver.disconnect();
      if (this.animObserver) this.animObserver.disconnect();
      this.initialised = false;
    }

    /* ------------------------------------------------------------ variants */

    findVariantById(id) {
      if (!id) return null;
      for (var i = 0; i < this.variants.length; i++) {
        if (this.variants[i].id === id) return this.variants[i];
      }
      return null;
    }

    /**
     * The selectors that drive variant choice, in cascade order. Each one is
     * constrained by the selectors before it, so adding a fourth option later
     * only means adding an entry here.
     */
    buildSelectors() {
      var self = this;
      var defs = [
        ['membership', this.memIndex, '[data-wc-membership-input]', '[data-wc-card]'],
        ['wine', this.wineIndex, '[data-wc-wine-input]', '[data-wc-wine-card]'],
        ['frequency', this.freqIndex, '[data-wc-frequency-input]', '[data-wc-freq-card]']
      ];

      this.selectors = [];
      defs.forEach(function (def) {
        var inputs = Array.prototype.slice.call(self.querySelectorAll(def[2]));
        if (!inputs.length) return;
        var index = def[1];
        // Frequency can be a line item property or a selling plan, in which
        // case it does not map to a variant option at all.
        var controls = typeof index === 'number' && index >= 0;
        if (def[0] === 'frequency' && self.freqSource !== 'option') controls = false;
        self.selectors.push({ key: def[0], index: index, controls: controls, inputs: inputs, card: def[3] });
      });
    }

    selectorFor(key) {
      return this.selectors.filter(function (sel) {
        return sel.key === key;
      })[0];
    }

    selectedValue(key) {
      var sel = this.selectorFor(key);
      if (!sel) return '';
      var checked = sel.inputs.filter(function (input) {
        return input.checked;
      })[0];
      return checked ? checked.value : '';
    }

    currentValues() {
      var self = this;
      var values = {};
      this.selectors.forEach(function (sel) {
        values[sel.key] = self.selectedValue(sel.key);
      });
      return values;
    }

    /**
     * Does an available variant exist with this value, given the values already
     * chosen for the selectors ahead of this one in the cascade?
     */
    valueAvailable(selector, value, values) {
      var self = this;
      if (!selector.controls) return true;
      var want = normalise(value);

      var upstream = [];
      for (var i = 0; i < this.selectors.length; i++) {
        var sel = this.selectors[i];
        if (sel === selector) break;
        if (sel.controls && values[sel.key]) upstream.push(sel);
      }

      return this.variants.some(function (variant) {
        if (!variant.available) return false;
        if (normalise(variant.options[selector.index]) !== want) return false;
        for (var j = 0; j < upstream.length; j++) {
          var u = upstream[j];
          if (normalise(variant.options[u.index]) !== normalise(values[u.key])) return false;
        }
        return true;
      });
    }

    /**
     * Resolve the variant matching every controlled selection. Options this
     * section does not control keep the value of the current variant.
     */
    resolveVariant(values) {
      var self = this;
      var base = this.currentVariant;
      var controlled = this.selectors.filter(function (sel) {
        return sel.controls;
      });

      var matches = this.variants.filter(function (variant) {
        for (var i = 0; i < controlled.length; i++) {
          var sel = controlled[i];
          var want = values[sel.key];
          if (!want) continue;
          if (normalise(variant.options[sel.index]) !== normalise(want)) return false;
        }
        if (base) {
          for (var j = 0; j < variant.options.length; j++) {
            var owned = controlled.some(function (sel) {
              return sel.index === j;
            });
            if (owned) continue;
            if (normalise(variant.options[j]) !== normalise(base.options[j])) return false;
          }
        }
        return true;
      });

      if (!matches.length) return null;
      var available = matches.filter(function (v) {
        return v.available;
      });
      return available.length ? available[0] : matches[0];
    }

    /* ----------------------------------------------------------- selectors */

    bindSelectors() {
      var self = this;
      var opts = { signal: this.signal };

      this.selectors.forEach(function (sel) {
        sel.inputs.forEach(function (input) {
          input.addEventListener('change', function () {
            self.clearErrors();
            self.refresh();
          }, opts);
        });
      });
    }

    /**
     * Recalculate every dependent piece of the interface from the current
     * selections. Never invents a price: everything comes from the variant.
     */
    refresh(options) {
      var self = this;
      var silent = options && options.silent;
      var values = this.currentValues();

      /*
       * Walk the selectors in cascade order. Each one is filtered by the
       * choices made ahead of it, and if the current choice has become
       * unavailable it moves to the first value that still works, so the
       * shopper is never left on a combination that cannot be bought.
       */
      this.selectors.forEach(function (sel) {
        var stillValid = false;
        var firstOk = null;

        sel.inputs.forEach(function (input) {
          var ok = self.valueAvailable(sel, input.value, values);
          input.disabled = !ok;
          if (ok) {
            input.removeAttribute('aria-disabled');
            if (!firstOk) firstOk = input;
            if (input.checked) stillValid = true;
          } else {
            input.setAttribute('aria-disabled', 'true');
          }
        });

        if (!stillValid && firstOk) {
          firstOk.checked = true;
          if (!silent && values[sel.key] && values[sel.key] !== firstOk.value) {
            self.announce(sel, firstOk.value);
          }
        }
        values[sel.key] = self.selectedValue(sel.key);
      });

      var membership = values.membership || '';
      var frequency = values.frequency || '';
      var wine = values.wine || '';

      var variant = this.resolveVariant(values);
      this.currentVariant = variant;

      if (this.variantInput) this.variantInput.value = variant ? variant.id : '';
      if (this.propertyInput) this.propertyInput.value = frequency || '';
      if (this.sellingPlanInput) this.sellingPlanInput.value = this.resolveSellingPlan(variant, frequency);

      this.updatePrice(variant, membership);
      this.updateCardPrices(values);
      this.updateSummary(variant, values);
      this.updateButton(variant);
      this.updateSticky(variant, membership);
      this.updateURL(variant);

      if (!silent) {
        this.dispatchEvent(
          new CustomEvent('wineclub:variant:change', {
            bubbles: true,
            detail: { variant: variant, membership: membership, wine: wine, frequency: frequency, values: values }
          })
        );
      }
    }

    resolveSellingPlan(variant, frequency) {
      if (this.freqSource !== 'selling_plan' || !variant || !variant.selling_plan_allocations) return '';
      var want = normalise(frequency);
      var match = variant.selling_plan_allocations.filter(function (alloc) {
        var name = alloc.selling_plan_name || (alloc.selling_plan && alloc.selling_plan.name);
        return normalise(name) === want;
      })[0];
      return match ? match.selling_plan_id : '';
    }

    announce(selector, value) {
      if (!this.live) return;
      this.live.textContent = value + ' selected.';
    }

    /* -------------------------------------------------------------- price */

    updatePrice(variant, membership) {
      if (!this.priceRoot) return;

      var amount = this.priceRoot.querySelector('[data-wc-price]');
      var compare = this.priceRoot.querySelector('[data-wc-compare]');
      var suffix = this.priceRoot.querySelector('[data-wc-price-suffix]');

      if (!variant) {
        if (amount) amount.textContent = '\u2014';
        if (compare) compare.hidden = true;
        if (suffix) suffix.textContent = '';
        return;
      }

      if (amount) amount.textContent = formatMoney(variant.price, this.moneyFormat);

      var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      this.priceRoot.classList.toggle('is-on-sale', !!onSale);
      if (compare) {
        if (onSale) {
          compare.textContent = formatMoney(variant.compare_at_price, this.moneyFormat);
          compare.hidden = false;
        } else {
          compare.hidden = true;
        }
      }

      if (suffix) {
        var parts = [];
        var suffixText = this.priceRoot.dataset.suffixText;
        if (suffixText) parts.push(suffixText);

        if (this.priceRoot.dataset.showPerBottle === 'true') {
          var count = this.bottleCount(membership);
          if (count > 0) {
            parts.push(
              formatMoney(Math.round(variant.price / count), this.moneyFormat) +
                ' ' +
                (this.priceRoot.dataset.perBottleSuffix || '')
            );
          }
        }
        suffix.textContent = parts.join(' \u00b7 ').trim();
      }
    }

    bottleCount(membership) {
      var configured = parseInt(this.priceRoot && this.priceRoot.dataset.bottleCount, 10);
      var fromValue = bottlesFrom(membership);
      if (fromValue > 0) return fromValue;
      return isNaN(configured) ? 0 : configured;
    }

    /** Each membership card shows the price of its own variant. */
    updateCardPrices(values) {
      var self = this;
      var priceEls = this.querySelectorAll('[data-wc-card-price]');
      var stockEls = this.querySelectorAll('[data-wc-card-stock]');
      if (!priceEls.length && !stockEls.length) return;

      var membership = this.selectorFor('membership');

      Array.prototype.forEach.call(priceEls, function (el) {
        // Price the card as if this membership were chosen, keeping the rest.
        var probe = Object.assign({}, values);
        probe.membership = el.dataset.value;
        var variant = self.resolveVariant(probe);
        el.textContent = variant ? formatMoney(variant.price, self.moneyFormat) : '';
      });

      Array.prototype.forEach.call(stockEls, function (el) {
        var ok = membership ? self.valueAvailable(membership, el.dataset.value, values) : true;
        el.textContent = ok ? '' : el.dataset.unavailable || el.textContent;
      });
    }

    /* ------------------------------------------------------------ summary */

    updateSummary(variant, values) {
      if (!this.summary) return;

      var valueEl = this.summary.querySelector('[data-wc-summary-value]');
      var priceEl = this.summary.querySelector('[data-wc-summary-price]');
      var template = this.summary.dataset.template || '';

      var membership = values.membership || '';
      var label = this.labelFor('[data-wc-card]', membership) || membership;
      var freqLabel = this.labelFor('[data-wc-freq-card]', values.frequency) || values.frequency || '';
      var wineLabel = this.labelFor('[data-wc-wine-card]', values.wine) || values.wine || '';

      var text = template
        .replace(/\[membership\]/g, label)
        .replace(/\[bottles\]/g, this.bottleCount(membership) || label)
        .replace(/\[wine\]/g, wineLabel)
        .replace(/\[frequency\]/g, freqLabel)
        .replace(/\[price\]/g, variant ? formatMoney(variant.price, this.moneyFormat) : '');

      if (valueEl) valueEl.textContent = text;
      if (priceEl) priceEl.textContent = variant ? formatMoney(variant.price, this.moneyFormat) : '\u2014';

      if (this.summary.dataset.animate === 'true' && !REDUCED_MOTION.matches) {
        this.summary.classList.remove('is-updating');
        void this.summary.offsetWidth;
        this.summary.classList.add('is-updating');
      }
    }

    /** Reads the visible label for a value so the summary uses editor overrides. */
    labelFor(selector, value) {
      if (!value) return '';
      var card = this.querySelector(selector + '[data-value="' + CSS.escape(value) + '"]');
      if (!card) return '';
      var title = card.querySelector('.wc-card__title, .wc-freq__title');
      return title ? title.textContent.trim() : '';
    }

    /* ------------------------------------------------------------- button */

    updateButton(variant) {
      if (!this.addButton || this.busy) return;
      var text = this.addText;
      var data = this.addButton.dataset;

      if (!variant) {
        this.addButton.disabled = true;
        if (text) text.textContent = data.labelUnavailable;
      } else if (!variant.available) {
        this.addButton.disabled = true;
        if (text) text.textContent = data.labelSoldOut;
      } else {
        this.addButton.disabled = false;
        if (this.addButton.dataset.wcState === 'idle' && text) text.textContent = data.labelDefault;
      }
    }

    updateSticky(variant, membership) {
      if (!this.sticky) return;
      var price = this.sticky.querySelector('[data-wc-sticky-price]');
      var label = this.sticky.querySelector('[data-wc-sticky-label]');
      if (price) price.textContent = variant ? formatMoney(variant.price, this.moneyFormat) : '\u2014';
      if (label) label.textContent = membership || '';
    }

    updateURL(variant) {
      if (!this.config.updateUrl || !variant || !window.history || !window.history.replaceState) return;
      if (!this.config.productUrl) return;
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    /* -------------------------------------------------------- description */

    bindDescription() {
      var toggle = this.querySelector('[data-wc-desc-toggle]');
      var body = this.querySelector('[data-wc-desc-body]');
      if (!toggle || !body) return;

      toggle.addEventListener(
        'click',
        function () {
          var collapsed = body.getAttribute('data-wc-collapsed') === 'true';
          body.setAttribute('data-wc-collapsed', collapsed ? 'false' : 'true');
          toggle.setAttribute('aria-expanded', collapsed ? 'true' : 'false');
          toggle.textContent = collapsed ? toggle.dataset.less : toggle.dataset.more;
        },
        { signal: this.signal }
      );
    }

    /* ------------------------------------------------------------ gallery */

    bindGallery() {
      var self = this;
      var gallery = this.querySelector('[data-wc-gallery]');
      if (!gallery) return;

      this.gallery = gallery;
      var track = gallery.querySelector('[data-wc-track]');
      if (!track) return;

      this.track = track;
      this.slides = Array.prototype.slice.call(track.querySelectorAll('[data-wc-slide]'));
      this.thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-wc-thumb]'));
      this.dots = Array.prototype.slice.call(gallery.querySelectorAll('[data-wc-dot]'));
      this.fraction = gallery.querySelector('[data-wc-fraction]');
      this.progress = gallery.querySelector('[data-wc-progress]');
      this.activeIndex = 0;

      var opts = { signal: this.signal };

      this.thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          self.goTo(parseInt(thumb.dataset.index, 10));
        }, opts);
      });

      this.dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          self.goTo(parseInt(dot.dataset.index, 10));
        }, opts);
      });

      var prev = gallery.querySelector('[data-wc-prev]');
      var next = gallery.querySelector('[data-wc-next]');
      if (prev) prev.addEventListener('click', function () { self.goTo(self.activeIndex - 1); }, opts);
      if (next) next.addEventListener('click', function () { self.goTo(self.activeIndex + 1); }, opts);

      if (gallery.dataset.keyboard === 'true') {
        track.setAttribute('tabindex', '0');
        track.addEventListener('keydown', function (event) {
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            self.goTo(self.activeIndex + 1);
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            self.goTo(self.activeIndex - 1);
          }
        }, opts);
      }

      // Track the visible slide without polling scroll position.
      if ('IntersectionObserver' in window && this.slides.length > 1) {
        this.slideObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting && entry.intersectionRatio > 0.55) {
                self.setActive(self.slides.indexOf(entry.target));
              }
            });
          },
          { root: track, threshold: [0.55] }
        );
        this.slides.forEach(function (slide) {
          self.slideObserver.observe(slide);
        });
      }

      if (gallery.dataset.zoom === 'modal') this.bindZoom(gallery);
      if (gallery.dataset.zoom === 'hover') this.bindHoverZoom();

      this.setActive(0);
    }

    goTo(index) {
      if (!this.slides || !this.slides.length) return;
      var clamped = Math.max(0, Math.min(index, this.slides.length - 1));
      var slide = this.slides[clamped];
      this.track.scrollTo({
        left: slide.offsetLeft - this.track.offsetLeft,
        behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth'
      });
      this.setActive(clamped);
    }

    setActive(index) {
      if (index < 0 || !this.slides || !this.slides.length) return;
      this.activeIndex = index;

      this.thumbs.forEach(function (thumb, i) {
        thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      this.dots.forEach(function (dot, i) {
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      if (this.fraction) this.fraction.textContent = index + 1 + ' / ' + this.slides.length;
      if (this.progress) {
        this.progress.style.width = ((index + 1) / this.slides.length) * 100 + '%';
      }

      var prev = this.gallery.querySelector('[data-wc-prev]');
      var next = this.gallery.querySelector('[data-wc-next]');
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === this.slides.length - 1;
    }

    bindHoverZoom() {
      var opts = { signal: this.signal };
      this.slides.forEach(function (slide) {
        if (!slide.classList.contains('wc-gallery__slide--zoomable')) return;
        slide.addEventListener('mousemove', function (event) {
          var rect = slide.getBoundingClientRect();
          var x = ((event.clientX - rect.left) / rect.width) * 100;
          var y = ((event.clientY - rect.top) / rect.height) * 100;
          slide.style.setProperty('--wc-zoom-origin', x + '% ' + y + '%');
        }, opts);
      });
    }

    bindZoom(gallery) {
      var self = this;
      var modal = gallery.querySelector('[data-wc-zoom-modal]');
      if (!modal || typeof modal.showModal !== 'function') return;

      var figure = modal.querySelector('[data-wc-zoom-figure]');
      var close = modal.querySelector('[data-wc-zoom-close]');
      var image = null;

      // Built on first open so the section never ships an empty <img src="">.
      var ensureImage = function () {
        if (image) return image;
        image = document.createElement('img');
        image.className = 'wc-gallery__zoom-img';
        image.setAttribute('data-wc-zoom-image', '');
        if (figure) figure.appendChild(image);
        return image;
      };
      var opts = { signal: this.signal };

      this.slides.forEach(function (slide) {
        if (!slide.classList.contains('wc-gallery__slide--zoomable')) return;
        slide.setAttribute('role', 'button');
        slide.setAttribute('tabindex', '0');
        var open = function () {
          var source = slide.dataset.zoomSrc;
          var img = slide.querySelector('img');
          var target = ensureImage();
          target.src = source || (img ? img.currentSrc || img.src : '');
          target.alt = img ? img.alt : '';
          self.zoomOrigin = slide;
          modal.showModal();
        };
        slide.addEventListener('click', open, opts);
        slide.addEventListener('keydown', function (event) {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            open();
          }
        }, opts);
      });

      if (close) close.addEventListener('click', function () { modal.close(); }, opts);
      modal.addEventListener('close', function () {
        if (self.zoomOrigin) self.zoomOrigin.focus();
      }, opts);
      modal.addEventListener('click', function (event) {
        if (event.target === modal) modal.close();
      }, opts);
    }

    /* ------------------------------------------------------------- sticky */

    bindSticky() {
      var self = this;
      if (!this.sticky) return;

      var button = this.sticky.querySelector('[data-wc-sticky-add]');
      var opts = { signal: this.signal };

      if (button) {
        button.addEventListener('click', function () {
          self.addToCart();
        }, opts);
      }

      var threshold = (this.config.stickyThreshold || 40) / 100;
      var onScroll = function () {
        if (self.scrollQueued) return;
        self.scrollQueued = true;
        window.requestAnimationFrame(function () {
          self.scrollQueued = false;
          var rect = self.getBoundingClientRect();
          var passed = rect.top < -(rect.height * threshold);
          var atEnd = rect.bottom < window.innerHeight;
          var visible = passed && !atEnd;
          self.sticky.setAttribute('data-wc-visible', visible ? 'true' : 'false');
          self.sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
          if (button) button.setAttribute('tabindex', visible ? '0' : '-1');
        });
      };

      window.addEventListener('scroll', onScroll, { passive: true, signal: this.signal });
      onScroll();
    }

    /* --------------------------------------------------------------- form */

    bindForm() {
      var self = this;
      if (!this.form) return;

      this.form.addEventListener(
        'submit',
        function (event) {
          event.preventDefault();
          self.addToCart();
        },
        { signal: this.signal }
      );
    }

    clearErrors() {
      if (this.errorBox) this.errorBox.setAttribute('data-wc-visible', 'false');
      Array.prototype.forEach.call(this.querySelectorAll('[data-wc-selector-error]'), function (el) {
        el.setAttribute('data-wc-visible', 'false');
      });
    }

    showError(message, scope) {
      var target = scope || this.errorBox;
      if (!target) return;
      if (target === this.errorBox && this.errorText) this.errorText.textContent = message;
      target.setAttribute('data-wc-visible', 'true');
    }

    validate() {
      var self = this;
      var ok = true;
      var scopes = { membership: '[data-wc-membership]', wine: '[data-wc-wine]', frequency: '[data-wc-frequency]' };

      this.selectors.forEach(function (sel) {
        if (self.selectedValue(sel.key)) return;
        var error = self.querySelector(scopes[sel.key] + ' [data-wc-selector-error]');
        self.showError('', error);
        ok = false;
      });
      if (!ok) {
        var firstInvalid = this.querySelector('[data-wc-selector-error][data-wc-visible="true"]');
        if (firstInvalid) firstInvalid.scrollIntoView({ block: 'center', behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' });
      }
      return ok;
    }

    setButtonState(state, label) {
      if (!this.addButton) return;
      this.addButton.dataset.wcState = state;
      if (this.addText && label) this.addText.textContent = label;

      var stickyText = this.sticky ? this.sticky.querySelector('[data-wc-sticky-text]') : null;
      var stickyBtn = this.sticky ? this.sticky.querySelector('[data-wc-sticky-add]') : null;
      if (stickyBtn) stickyBtn.dataset.wcState = state;
      if (stickyText && label) stickyText.textContent = label;
    }

    /* --------------------------------------------------------------- cart */

    async addToCart() {
      if (this.busy) return;
      this.clearErrors();

      if (!this.validate()) return;

      var variant = this.currentVariant;
      var data = this.addButton ? this.addButton.dataset : {};

      if (!variant) {
        this.showError(this.strings.unavailable || data.labelUnavailable || '');
        return;
      }
      if (!variant.available) {
        this.showError(this.strings.soldOut || data.labelSoldOut || '');
        return;
      }

      this.busy = true;
      if (this.addButton) this.addButton.disabled = true;
      this.setButtonState('loading', data.labelLoading);
      if (this.live) this.live.textContent = this.strings.loading || data.labelLoading || '';

      var body = new FormData(this.form);
      body.set('id', variant.id);

      var sections = this.cartSectionIds();
      if (sections.length) {
        body.set('sections', sections.join(','));
        body.set('sections_url', window.location.pathname);
      }

      try {
        var response = await fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root + 'cart/add.js'
          : '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: body
        });

        var payload = await response.json();

        if (!response.ok || payload.status) {
          throw new Error(payload.description || payload.message || this.strings.cartError || '');
        }

        var wantsDrawer = data.openDrawer !== 'false';
        var result = this.deliverToCart(payload, wantsDrawer);

        this.setButtonState('success', data.labelSuccess);
        if (this.live) this.live.textContent = this.strings.itemAdded || data.labelSuccess || '';

        this.dispatchCartEvents(payload);

        if (wantsDrawer && !result.opened) {
          // No drawer exists in this theme: fall back to the cart page.
          window.location.href =
            (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
              ? window.Shopify.routes.root
              : '/') + 'cart';
          return;
        }

        var self = this;
        var duration = parseInt(data.successDuration, 10) || 3000;
        if (this.successTimer) clearTimeout(this.successTimer);
        this.successTimer = setTimeout(function () {
          self.setButtonState('idle', data.labelDefault);
          self.busy = false;
          self.updateButton(self.currentVariant);
        }, duration);
      } catch (error) {
        this.busy = false;
        this.setButtonState('idle', data.labelError || data.labelDefault);
        if (this.addButton) this.addButton.disabled = false;
        var message = error.message || this.strings.cartError || '';
        this.showError(message);
        if (this.live) this.live.textContent = message;
      }
    }

    /**
     * Section ids the theme's cart UI needs re-rendered. Detected from the DOM
     * so this works with Dawn, Dawn forks and custom drawers alike.
     */
    cartSectionIds() {
      var ids = [];
      var add = function (element) {
        if (!element) return;
        var wrapper = element.closest('[id^="shopify-section-"]');
        if (!wrapper) return;
        var id = wrapper.id.replace('shopify-section-', '');
        if (ids.indexOf(id) === -1) ids.push(id);
      };

      var drawer = this.findDrawer();
      add(drawer);

      var bubbleSelectors = [
        '#cart-icon-bubble',
        '.cart-count-bubble',
        '[data-cart-count]',
        '#CartCount',
        '.js-cart-count',
        'cart-notification'
      ];
      for (var i = 0; i < bubbleSelectors.length; i++) {
        add(document.querySelector(bubbleSelectors[i]));
      }

      return ids;
    }

    findDrawer() {
      var selectors = [
        'cart-drawer',
        'cart-notification',
        '#CartDrawer',
        '#cart-drawer',
        '[data-cart-drawer]',
        '.drawer--cart',
        '.cart-drawer',
        '#sidebar-cart'
      ];
      for (var i = 0; i < selectors.length; i++) {
        var found = document.querySelector(selectors[i]);
        if (found) return found;
      }
      return null;
    }

    /** Swap in any sections the Cart API returned. */
    renderCart(payload) {
      if (!payload || !payload.sections) return;
      Object.keys(payload.sections).forEach(function (id) {
        var target = document.getElementById('shopify-section-' + id);
        var html = payload.sections[id];
        if (!target || typeof html !== 'string') return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var source = parsed.querySelector('#shopify-section-' + id);
        target.innerHTML = source ? source.innerHTML : html;
      });
    }

    dispatchCartEvents(payload) {
      var detail = { source: 'wine-club-product', productVariantId: payload.variant_id, cartData: payload };

      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: detail }));
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: detail }));
      document.dispatchEvent(new CustomEvent('product:added', { bubbles: true, detail: detail }));

      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
        try {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, detail);
        } catch (e) {
          /* theme does not use pub/sub */
        }
      }
    }

    /**
     * Hand the Cart API response to whatever cart UI the theme already ships.
     * The theme's own renderer is preferred, because replacing the drawer
     * markup ourselves would throw away its internal state. Returns
     * { rendered, opened } so the caller can fall back to /cart when the theme
     * has no drawer at all.
     */
    deliverToCart(payload, shouldOpen) {
      var drawer = this.findDrawer();

      if (!drawer) {
        this.renderCart(payload);
        return { rendered: true, opened: false };
      }

      drawer.classList.remove('is-empty');

      // Dawn and most forks: renderContents swaps the markup and opens the drawer.
      if (shouldOpen && typeof drawer.renderContents === 'function') {
        try {
          drawer.renderContents(payload);
          return { rendered: true, opened: true };
        } catch (e) {
          /* fall through to the generic paths below */
        }
      }

      this.renderCart(payload);
      if (!shouldOpen) return { rendered: true, opened: false };

      // The drawer node may have been replaced by renderCart.
      drawer = this.findDrawer() || drawer;

      var openers = ['open', 'show', 'openDrawer'];
      for (var i = 0; i < openers.length; i++) {
        if (typeof drawer[openers[i]] === 'function') {
          try {
            drawer[openers[i]]();
            return { rendered: true, opened: true };
          } catch (e) {
            /* try the next strategy */
          }
        }
      }

      document.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));

      var toggle = document.querySelector(
        '[data-cart-drawer-toggle], [href$="/cart"][data-drawer-open], .js-drawer-open-cart'
      );
      if (toggle) {
        toggle.click();
        return { rendered: true, opened: true };
      }

      // Last resort: reveal it using the class names themes commonly use.
      drawer.classList.add('active', 'is-open', 'drawer--is-open');
      drawer.setAttribute('open', '');
      drawer.removeAttribute('aria-hidden');

      var focusTarget = drawer.querySelector(
        'button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"])'
      );
      if (focusTarget) focusTarget.focus();

      return { rendered: true, opened: true };
    }

    /* ---------------------------------------------------------- animation */

    setupAnimation() {
      var self = this;
      if (this.dataset.wcAnimate !== 'true' || REDUCED_MOTION.matches) return;
      if (!('IntersectionObserver' in window)) return;

      var targets = Array.prototype.slice.call(this.querySelectorAll('.wc-anim'));
      if (!targets.length) return;

      var stagger = this.config.animationStagger || 0;
      var once = this.config.animateOnce !== false;

      targets.forEach(function (el, i) {
        if (stagger && !el.style.getPropertyValue('--wc-anim-delay')) {
          el.style.setProperty('--wc-anim-delay', i * stagger + 'ms');
        }
      });

      this.animObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              if (once) self.animObserver.unobserve(entry.target);
            } else if (!once) {
              entry.target.classList.remove('is-visible');
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
      );

      targets.forEach(function (el) {
        self.animObserver.observe(el);
      });
    }
  }

  customElements.define('wine-club-product', WineClubProduct);
})();